import type { StateConfig } from '@/entities/state/model'

export const stateDefinitionConfigMock: StateConfig = {
  collectCodeEligibleOrderTypes: ['Click & Collect', 'Click & Collect Rapid'],
  hsFieldsPriority: [
    'Customer Name',
    'Order Number',
    'Collection Window',
    'Van Seq',
    'Ambient',
    'Bulk Ambient',
    'Chilled',
    'Bulk Chilled',
    'Frozen',
    'Hot Food',
    'Restricted',
    'Order Type',
  ],
  features: {
    realtimeUpdates: true,
    hideSearchBar: false,
    collectCodeEnabled: true,
    zebraBarcodeEnabled: false,
    digitisedPoaEnabled: true,
    collectTMInitialsEnabled: true,
    crateEmojisEnabled: true,
    waitTime: {
      endState: ['Completed'],
      startState: ['Arrived', 'DeliveringToCustomer'],
      alert: 3000, // 5 minutes in seconds - warning threshold
      goal: 6000, // 10 minutes in seconds - error threshold
    },
    orderGrouping: {
      enabled: true,
      groupPrefix: 'group',
    },
  },
  categories: [
    'Delivering to Customer',
    'Arrived',
    'On The Way',
    'Ready To Pick',
    'Ready To Collect',
    'Overdue',
  ],
  orderCardFields: [
    'vanNumber',
    'sequenceNumber',
    'orderType',
    'driverName',
  ],
  states: [
    {
      canForceTransition: false,
      displayState: null,
      isError: false,
      shouldNotify: false,
      progressionLevel: 1,
      canAcknowledge: false,
      eventTriggers: ['REGISTRATION'],
      name: 'Registered',
      isActive: true,
      analyticsCategory: 'registered',
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'ON THE WAY',
        color: '#8523f4',
        categoryTitle: 'On The Way',
        orderDetailsSummary: '{customerName} is on the way',
        stateDisplayName: 'On The Way',
        showCollectionTime: true,
      },
      isError: false,
      shouldNotify: true,
      progressionLevel: 10,
      canAcknowledge: false,
      eventTriggers: ['NO_UPDATES_5_MINS', 'TEMPO_TIMEOUT', 'onTheWay'],
      name: 'OnTheWay',
      isActive: true,
      analyticsCategory: 'onTheWay',
    },
    {
      canForceTransition: false,
      displayState: {
        orderDetailsTitle: '{etaMinutes} AWAY',
        color: '#8523f4',
        categoryTitle: 'On The Way',
        orderDetailsSummary: '{customerName} is arriving {etaDirectionPreposition} {arrivalTime}.',
        stateDisplayName: 'On The Way',
      },
      isError: false,
      shouldNotify: true,
      progressionLevel: 10,
      canAcknowledge: false,
      eventTriggers: ['TEMPO_UPDATE'],
      name: 'TempoOnTheWay',
      isActive: true,
      analyticsCategory: 'onTheWay',
    },
    {
      canForceTransition: false,
      displayState: {
        orderDetailsTitle: '{etaMinutes} AWAY',
        color: '#343a40',
        categoryTitle: 'On The Way',
        orderDetailsSummary: 'Driver is arriving {etaDirectionPreposition} {arrivalTime}.',
        stateDisplayName: 'On The Way',
      },
      isError: false,
      shouldNotify: true,
      progressionLevel: 10,
      canAcknowledge: false,
      eventTriggers: ['PARTNER_ETA'],
      name: 'PartnerOnTheWay',
      isActive: true,
      analyticsCategory: 'onTheWay',
    },
    {
      canForceTransition: false,
      displayState: {
        orderDetailsTitle: 'STOPPED',
        color: '#FA5252',
        categoryTitle: 'On The Way',
        orderDetailsSummary: '{customerName} stopped tracking journey at {formattedLastTransitionTime}',
        stateDisplayName: 'Stopped',
        quickButtons: [
          {
            action: 'ACKNOWLEDGE',
            label: 'Clear',
          },
        ],
      },
      isError: true,
      shouldNotify: true,
      progressionLevel: 15,
      canAcknowledge: true,
      eventTriggers: ['TEMPO_CANCELLED'],
      name: 'Stopped',
      isActive: false,
      analyticsCategory: 'haltFail',
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'CANCELLED',
        color: '#FA5252',
        categoryTitle: 'On The Way',
        orderDetailsSummary: '{customerName}\'s order was cancelled',
        stateDisplayName: 'Cancelled',
        quickButtons: [
          {
            action: 'ACKNOWLEDGE',
            label: 'Clear',
          },
        ],
      },
      isError: true,
      shouldNotify: true,
      progressionLevel: 25,
      canAcknowledge: false,
      eventTriggers: ['cancelled'],
      name: 'Cancelled',
      isActive: false,
      analyticsCategory: 'haltFail',
    },
    {
      displayState: {
        orderDetailsTitle: 'CUSTOMER ARRIVED',
        color: '#297d2f',
        orderDetailsSummary: '{customerName} arrived at {formattedLastTransitionTime}.',
        categoryTitle: 'Arrived',
        showSuccessMessage: false,
        stateDisplayName: 'Arrived',
        quickButtons: [
          {
            action: 'DeliveringToCustomer',
            label: 'We\'re on it',
          },
        ],
      },
      canForceTransition: true,
      isError: false,
      shouldNotify: true,
      notificationTypes: ['consolidation'],
      progressionLevel: 20,
      canAcknowledge: true, // This enables animation for unacknowledged orders
      eventTriggers: ['GEOTRIGGER', 'arrival'],
      name: 'Arrived',
      isActive: true,
      analyticsCategory: 'arrived',
    },
    {
      displayState: {
        orderDetailsTitle: 'DELIVERING TO CUSTOMER',
        color: '#343a40',
        orderDetailsSummary: '{customerName} arrived at {formattedWaitTimeStart}.',
        categoryTitle: 'Delivering to Customer',
        showSuccessMessage: true,
        stateDisplayName: 'Delivering',
        quickButtons: [
          {
            action: 'Completed',
            label: 'Complete',
          },
        ],
      },
      canForceTransition: true,
      isError: false,
      shouldNotify: false,
      progressionLevel: 21,
      canAcknowledge: true, // Changed to true to enable animation for this state
      eventTriggers: ['deliveringToCustomer'],
      name: 'DeliveringToCustomer',
      isActive: true,
      analyticsCategory: 'arrived',
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'COMPLETED',
        color: '#343a40',
        categoryTitle: 'Arrived',
        orderDetailsSummary: '{customerName}\'s order is completed.',
        stateDisplayName: 'Completed',
      },
      isError: false,
      shouldNotify: false,
      isVerified: false,
      progressionLevel: 100,
      canAcknowledge: false,
      eventTriggers: ['completed'],
      name: 'Completed',
      isActive: false,
      analyticsCategory: 'haltSucceed',
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'READY TO COLLECT',
        color: '#343a40',
        categoryTitle: 'Ready To Collect',
        orderDetailsSummary: '{customerName}\'s order is ready to collect.',
        stateDisplayName: 'Ready To Collect',
      },
      isError: false,
      shouldNotify: false,
      progressionLevel: 5,
      canAcknowledge: false,
      eventTriggers: ['pickCompleted'],
      name: 'ReadyToCollect',
      isActive: true,
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'READY TO PICK',
        color: '#FA5252',
        categoryTitle: 'Ready To Pick',
        orderDetailsSummary: '{customerName}\'s order is ready to pick.',
        stateDisplayName: 'Ready To Pick',
        quickButtons: [
          {
            action: 'ReadyToCollect',
            label: 'Pick Complete',
          },
        ],
      },
      isError: false,
      shouldNotify: true,
      progressionLevel: 3,
      canAcknowledge: false,
      eventTriggers: ['readyToPick'],
      name: 'ReadyToPick',
      isActive: true,
    },
    {
      canForceTransition: true,
      displayState: {
        orderDetailsTitle: 'ORDER OVERDUE',
        color: '#FA5252',
        categoryTitle: 'Overdue',
        orderDetailsSummary: '{customerName}\'s order was not collected on time.',
        stateDisplayName: 'Order Overdue',
      },
      isError: false,
      shouldNotify: false,
      progressionLevel: 6,
      canAcknowledge: false,
      eventTriggers: ['orderOverdue'],
      name: 'OrderOverdue',
      isActive: true,
    },
    {
      canForceTransition: false,
      displayState: {
        orderDetailsTitle: 'COMPLETED',
        color: '#37B24D',
        categoryTitle: 'Completed',
        orderDetailsSummary: '{customerName}\'s order has been completed.',
        stateDisplayName: 'Completed',
      },
      isError: false,
      shouldNotify: false,
      progressionLevel: 100,
      canAcknowledge: false,
      eventTriggers: ['completed'],
      name: 'Completed',
      isActive: true,
    },
  ],
  missingCrateReasons: [
    { reason: 'Crate merged', enabled: true },
    { reason: 'Crate couldn\'t be scanned', enabled: true },
    { reason: 'Crate not on shelf', enabled: true },
  ],
  verificationErrorCodes: [
    {
      errorCode: 'FRIEND',
      description: 'Order is for someone else (relative/friend)',
      shouldDisplay: true,
    },
    {
      errorCode: 'BUSINESS',
      description: 'Order is for a business or organisation',
      shouldDisplay: true,
    },
    {
      errorCode: 'DONT-KNOW',
      description: 'Customer can\'t remember or doesn\'t know.',
      shouldDisplay: true,
    },
    {
      errorCode: 'UNWILLING',
      description: 'Customer is unwilling to provide.',
      shouldDisplay: true,
    },
    {
      errorCode: 'OTHER',
      description: 'Other',
      shouldDisplay: true,
    },
  ],
}
